import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { CompanyUserService } from '@treasury/domain/company/user';
import { AccountAccess } from '@treasury/domain/company/user/entities/access';
import { CompanyUserProductEntitlements } from '@treasury/domain/company/user/entities/entitlements';

import { TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/jh-ui/tm-checkbox';
import '@treasury/presentation/components/jha-wc/tm-table';
// eslint-disable-next-line no-duplicate-imports
import {
    NoPagination,
    TmTableClickEvent,
    TmTableOptions,
} from '@treasury/presentation/components/jha-wc/tm-table';
import {
    SideDrawerButton,
    SideDrawerButtonPosture,
    TmSideDrawerService,
} from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DiscardChangesConfirmationData } from '../../../../../overlays/discard-changes-drawer/discard-changes.component';
import '../overlays/account-permissions-drawer.component';

@customElement('tm-company-user-account-list')
export class CompanyUserAccountList extends TmBaseComponent {
    @property({ type: Number })
    public companyId?: number;

    @property({ type: Number })
    public userId?: number;

    @property()
    public entitlements?: CompanyUserProductEntitlements;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly sideDrawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @state()
    private accounts: AccountAccess[] = [];

    @state()
    private loading = true;

    private get isBoApp() {
        return this.config.app === AppType.BackOffice;
    }

    public render() {
        if (this.loading) {
            return html`<jha-progress></jha-progress> Loading users's accounts...`;
        }
        return html`${this.renderAccounts()}`;
    }

    protected async firstUpdated() {
        try {
            this.loading = true;
            await this.hydrateAccountAccess();
        } catch {
            this.toastService.show('Failed to load entitlements', TmToastPosture.Danger, true);
        } finally {
            this.loading = false;
        }
    }

    private renderAccounts() {
        if (this.entitlements?.accounts?.length === 0) {
            return nothing;
        }

        return html` <div class="text-lg font-semibold">Accounts</div>
            <div class="mt-10">
                <tm-table
                    @row-click=${this.handleRowClick}
                    .options=${this.options}
                    .items=${this.accounts}
                ></tm-table>
            </div>`;
    }

    private get options(): TmTableOptions<AccountAccess> {
        return {
            displayAllView: false,
            pagination: NoPagination,
            columns: {
                definitions: [
                    { prop: 'accountName', label: 'Account name', searchable: true },
                    { prop: 'accountNumber', label: 'Account number', searchable: true },
                    { prop: 'cifNumber', label: 'CIF number', searchable: true },
                    { prop: 'accountType', label: 'Type', searchable: true },
                ],
            },
        };
    }

    private async handleRowClick(e: TmTableClickEvent<AccountAccess>) {
        const { item } = e.detail;
        const accountInfo = item;
        const account = this.entitlements?.accounts.find(
            account => account.number === accountInfo.accountNumber
        );

        const buttons: SideDrawerButton<DiscardChangesConfirmationData>[] = [
            {
                label: 'Save',
                posture: SideDrawerButtonPosture.Primary,
                reason: DynamicComponentExitReason.Confirm,
            },
            {
                label: 'Cancel',
                posture: SideDrawerButtonPosture.Tertiary,
                reason: DynamicComponentExitReason.Cancel,
            },
        ];

        const result = await this.sideDrawerService.open<DiscardChangesConfirmationData>(
            () =>
                html`<tm-company-user-account-permissions-drawer
                    .accountInfo=${accountInfo}
                    .accountClaims=${account}
                ></tm-company-user-account-permissions-drawer>`,
            `${accountInfo.accountName} (${accountInfo.accountNumber}) - Account Permissions`,

            // Hack: We are having to cast to an unknown type because the side-drawer service
            // is not properly handling our data type. This is a temporary fix until the service
            // is updated to handle the correct data type.
            { align: 'right', buttons: buttons as SideDrawerButton<unknown>[] }
        );

        const exitReasons = [
            DynamicComponentExitReason.Cancel,
            DynamicComponentExitReason.ForceClose,
        ];

        if (exitReasons.includes(result.reason)) {
            return;
        }

        try {
            // if (!this.userId || !this.companyId) {
            //     throw new Error('User ID and company ID are required to discard company user changes');
            // }
            // const success = await this.companyUserService.discardCompanyUserChanges(
            //     this.companyId,
            //     this.userId,
            //     comments
            // );
            // if (!success) {
            //     throw new Error('Failed to save company user account permission changes');
            // }

            this.toastService.show(
                'Company user account permission changes saved successfully',
                TmToastPosture.Success
            );
        } catch {
            this.toastService.show(
                'Failed to save company user account permission changes',
                TmToastPosture.Danger,
                true
            );
        }
    }

    private async hydrateAccountAccess() {
        try {
            if (this.isBoApp && !this.companyId) {
                throw new Error('Company ID is required');
            }
            if (!this.userId) {
                throw new Error('User ID is required');
            }
            const { accounts } = await this.companyUserService.getCompanyUserAccountAccess(
                this.userId,
                this.companyId
            );
            this.accounts = accounts.filter(account => account.hasPendingUserAccess);
        } catch {
            this.toastService.show('Failed to load account access', TmToastPosture.Danger, true);
        }
    }
}
